import { useEffect, useMemo, useState } from 'react'
import { Skeleton, Stack, Text } from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'
import { EmailTemplateEditor } from '../components/EmailTemplateEditor'
import { PageHeader } from '../components/ui/PageHeader'
import { useTranslation } from '../i18n'
import {
  fetchGlobalTemplates, fetchTemplateDefaults, resolveContent, saveGlobalTemplate,
  sendTestEmail,
} from '../services/emailTemplates'

/**
 * The sysadmin-owned default copy for every message the platform sends. These
 * are what a new event is prefilled from, and what the senders fall back to
 * when an event has no override of its own.
 */
export default function EmailTemplatesPage() {
  const { isSysAdmin } = useAuth()
  const { t, lang } = useTranslation()
  const [defaults, setDefaults] = useState(null)
  const [stored, setStored] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isSysAdmin) return undefined
    let cancelled = false
    Promise.all([fetchTemplateDefaults(), fetchGlobalTemplates()])
      .then(([bundled, globals]) => {
        if (cancelled) return
        setDefaults(bundled)
        setStored(globals)
      })
      .catch((err) => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [isSysAdmin])

  // Memoized: the editors re-seed their draft when this identity changes, so a
  // fresh object every render would discard whatever the admin is typing.
  const editors = useMemo(() => {
    if (!defaults || !stored) return []
    return Object.entries(defaults.specs).map(([templateId, spec]) => ({
      templateId,
      spec,
      defaultContent: defaults.templates[templateId],
      samples: defaults.samples?.[templateId],
      content: resolveContent({
        stored: stored[templateId],
        bundled: defaults.templates[templateId],
        spec,
      }),
    }))
  }, [defaults, stored])

  if (!isSysAdmin) return <Text color="var(--mc-steel)">{t('common.noPermission')}</Text>

  const loading = !error && (!defaults || !stored)

  return (
    <Stack gap={6}>
      <PageHeader title={t('templates.title')} subtitle={t('templates.subtitle')} />
      {error && <Text color="red.300">{error}</Text>}
      {loading ? (
        <Stack gap={4}>{[1, 2, 3].map((i) => <Skeleton key={i} height="240px" borderRadius="md" />)}</Stack>
      ) : (
        <Stack gap={4}>
          {editors.map(({ templateId, spec, samples, content, defaultContent }) => (
            <EmailTemplateEditor
              key={templateId}
              templateId={templateId}
              spec={spec}
              samples={samples}
              initialLanguage={lang}
              defaultContent={defaultContent}
              content={content}
              onSave={async (next) => {
                await saveGlobalTemplate(templateId, next)
                setStored((prev) => ({ ...prev, [templateId]: next }))
              }}
              onSendTest={(args) => sendTestEmail({ templateId, ...args })}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
